import { CSSProperties, ReactElement, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, styled } from "@mui/material";
import { DotLegalFullScreenSpinner } from "@dotlegal/dotlegal-ui-components";
import { Document, Page, pdfjs } from "react-pdf";
import { List, ListImperativeAPI } from "react-window";
import pdfjsWorker from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { useDocumentPreviewStyles } from "./DocumentPreview.styles";
import DocumentPreviewToolbar from "./DocumentPreviewToolbar";
import { IDocumentNavigation, IDocumentPreviewToolbarAction } from "./DocumentPreview.types";
import { useDocumentKeyboardNavigation } from "./DocumentPreview.hooks";
import { useCtrlWheelZoom } from "./useCtrlWheelZoom";
import DocumentPreviewFailed from "./DocumentPreviewFailed";
import {
    buildCitationRangeInDom,
    buildSingleNeedleRegex,
    CITATION_OVERLAY_COLOR,
    CitationAnchors,
    CitationPageRole,
    computeCitationPageRoles,
    deriveCitationAnchorSets,
    getBandRects,
    getMatchedSliceRects,
    normalizeForSearch,
} from "./documentSearch";
import TextSelectionLayer from "./textSelection/TextSelectionLayer";
import { PDF_TEXT_SELECTION_DOM_CONFIG } from "./textSelection/textSelection.domConfigs";
import { ITextSelectionConfig, SectionResolver } from "./textSelection/textSelection.types";

pdfjs.GlobalWorkerOptions.workerSrc = pdfjsWorker;

const PDF_WASM_URL = "/pdf-wasm/";

const PAGE_GAP = 16;
const NAVIGATION_DEBOUNCE_MS = 500;
const MIN_SEARCH_LENGTH = 2;
const CITATION_OVERLAY_VERTICAL_PADDING = 4;
const TEXT_LAYER_PROBE_PAGES = 3;

type HighlightMap = Map<number, Map<number, [number, number][]>>;

// One region of a citation as it lands on a page: which anchors to look for there and how much of
// the page they cover. A prose citation is one region; a table citation is one per row.
interface PageCitation {
    anchors: CitationAnchors;
    role: CitationPageRole;
}

interface PageRowProps {
    pageHeight: number;
    zoomLevel: number;
    pageWrapperStyle: object;
    highlightMap: HighlightMap;
    hasSearch: boolean;
    pageCitations: Map<number, Array<PageCitation>>;
}

interface OverlayRect {
    top: number;
    left: number;
    width: number;
    height: number;
}

function PageRow(
    props: {
        ariaAttributes: { "aria-posinset": number; "aria-setsize": number; role: "listitem" };
        index: number;
        style: CSSProperties;
    } & PageRowProps
): ReactElement {
    const { index, style, pageHeight, zoomLevel, pageWrapperStyle, highlightMap, hasSearch, pageCitations } = props;
    const pageNumber = index + 1;
    const overlayParentRef = useRef<HTMLDivElement>(null);
    const [overlayRects, setOverlayRects] = useState<OverlayRect[]>([]);
    const citations = pageCitations.get(pageNumber);
    // Held in a ref so the text-layer-success callback always reads the latest value — react-pdf
    // caches the original `onRenderTextLayerSuccess` prop from mount, so closure-captured values
    // would go stale as soon as the user clicks a different citation.
    const citationsRef = useRef(citations);
    citationsRef.current = citations;

    const customTextRenderer = useMemo(() => {
        if (!hasSearch) return undefined;
        const pageHighlights = highlightMap.get(pageNumber);
        if (!pageHighlights || pageHighlights.size === 0) return undefined;

        return (textItem: { str: string; itemIndex: number }) => {
            const ranges = pageHighlights.get(textItem.itemIndex);
            if (!ranges || ranges.length === 0) return textItem.str;

            const sorted = [...ranges].sort((a, b) => a[0] - b[0]);
            let result = "";
            let lastEnd = 0;
            for (const [start, end] of sorted) {
                result += escapeHtml(textItem.str.slice(lastEnd, start));
                result += `<mark>${escapeHtml(textItem.str.slice(start, end))}</mark>`;
                lastEnd = end;
            }
            result += escapeHtml(textItem.str.slice(lastEnd));
            return result;
        };
    }, [hasSearch, highlightMap, pageNumber]);

    // Reads the page's citations from a ref so callers invoked through a cached react-pdf callback
    // (which captured the original render's closure) still see the current citation. Always
    // clears previous overlay rects first — if the text layer isn't ready yet we want stale
    // overlays from a prior citation gone, not lingering on screen until the new ones can paint.
    //
    // Walks the rendered text-layer DOM directly to find each citation range — no reliance on
    // pdf.js item-index alignment with rendered spans. The role tells us how much of this page
    // a region covers: a 'middle' page is wholly within it and gets fully highlighted; a
    // 'start' or 'end' page only finds one of the two anchors; a 'single' page finds both.
    //
    // Stable identity (useCallback with []) so the `onRenderTextLayerSuccess` prop we pass to
    // <Page> doesn't change every render — react-pdf's TextLayer effect re-runs when that
    // callback reference changes, which would tear down and rebuild the text layer on every
    // parent render and break mouse text selection.
    const recomputeOverlay = useCallback(() => {
        const parent = overlayParentRef.current;
        if (!parent) return;
        const citations = citationsRef.current;
        const textLayer = parent.querySelector<HTMLElement>(".react-pdf__Page__textContent");
        if (!citations || citations.length === 0 || !textLayer) {
            setOverlayRects((prev) => (prev.length === 0 ? prev : []));
            return;
        }
        const parentRect = parent.getBoundingClientRect();
        const next = citations.flatMap((citation) => buildOverlayRects(textLayer, citation, parentRect));
        // Skip the state update when rects are unchanged — prevents render churn that
        // would otherwise re-trigger react-pdf's text-layer rebuild and break selection.
        setOverlayRects((prev) => (areSameRects(prev, next) ? prev : next));
    }, []);

    // Recompute when this page's citations change — covers the case where the text layer
    // rendered before `pageCitations` arrived (so the callback path fired with nothing to draw
    // and never re-fired). Without this, late-arriving citations never get an overlay even when
    // the anchors matched. Also handles transitioning to the no-citation state by clearing rects.
    useEffect(() => {
        recomputeOverlay();
    }, [citations, recomputeOverlay]);

    const handleTextLayerSuccess = recomputeOverlay;

    return (
        <div style={{ ...style, display: "flex", justifyContent: "safe center", paddingBottom: PAGE_GAP }}>
            <div ref={overlayParentRef} style={{ position: "relative" }}>
                <Box sx={pageWrapperStyle}>
                    <Page
                        pageNumber={pageNumber}
                        height={pageHeight > 0 ? Math.round(pageHeight * zoomLevel) : undefined}
                        loading=""
                        customTextRenderer={customTextRenderer}
                        onRenderTextLayerSuccess={handleTextLayerSuccess}
                    />
                </Box>
                {overlayRects.map((r, i) => (
                    <div
                        key={i}
                        style={{
                            position: "absolute",
                            top: r.top,
                            left: r.left,
                            width: r.width,
                            height: r.height,
                            background: CITATION_OVERLAY_COLOR,
                            pointerEvents: "none",
                        }}
                    />
                ))}
            </div>
        </div>
    );
}

// A prose region gets one band across the text layer. An exact region (a table row) gets one rectangle per
// matched text span instead, since the text beside it on the line is another cell.
function buildOverlayRects(textLayer: HTMLElement, citation: PageCitation, parentRect: DOMRect): Array<OverlayRect> {
    const rangePos = buildCitationRangeInDom(textLayer, citation.anchors, citation.role);
    if (!rangePos) return [];
    try {
        const rects = citation.anchors.exact ? getMatchedSliceRects(textLayer, rangePos) : getBandRects(textLayer, rangePos);
        return rects.map((r) => toOverlayRect(r, parentRect));
    } catch {
        // Range construction can throw on detached nodes during quick navigation — skip.
        return [];
    }
}

function toOverlayRect(r: DOMRect, parentRect: DOMRect): OverlayRect {
    return {
        top: r.top - parentRect.top - CITATION_OVERLAY_VERTICAL_PADDING,
        left: r.left - parentRect.left,
        width: r.width,
        height: r.height + CITATION_OVERLAY_VERTICAL_PADDING * 2,
    };
}

function areSameRects(a: OverlayRect[], b: OverlayRect[]): boolean {
    return (
        a.length === b.length &&
        a.every((rect, i) => rect.top === b[i].top && rect.left === b[i].left && rect.width === b[i].width && rect.height === b[i].height)
    );
}

function escapeHtml(text: string) {
    return text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function searchDocument(
    searchText: string,
    pageTexts: Map<number, string>
): { pagesWithMatches: number[]; pageHighlightRanges: Map<number, [number, number][]> } {
    return searchDocumentByRegex(buildSingleNeedleRegex(searchText), pageTexts);
}

function searchDocumentByRegex(
    regex: RegExp,
    pageTexts: Map<number, string>
): { pagesWithMatches: number[]; pageHighlightRanges: Map<number, [number, number][]> } {
    const empty = { pagesWithMatches: [] as number[], pageHighlightRanges: new Map<number, [number, number][]>() };
    if (pageTexts.size === 0) return empty;

    let fullText = "";
    const pageBounds: { pageNum: number; start: number; end: number }[] = [];
    const sortedPages = [...pageTexts.keys()].sort((a, b) => a - b);
    for (const pageNum of sortedPages) {
        const text = pageTexts.get(pageNum)!;
        if (fullText.length > 0) fullText += " ";
        const start = fullText.length;
        fullText += text;
        pageBounds.push({ pageNum, start, end: fullText.length });
    }

    const normalizedFullText = normalizeForSearch(fullText);
    const matchedPages = new Set<number>();
    const pageHighlightRanges = new Map<number, [number, number][]>();
    regex.lastIndex = 0;
    let match;
    while ((match = regex.exec(normalizedFullText)) !== null) {
        if (match[0].length === 0) {
            regex.lastIndex++;
            continue;
        }
        const matchStart = match.index;
        const matchEnd = matchStart + match[0].length;
        for (const { pageNum, start, end } of pageBounds) {
            if (matchEnd > start && matchStart < end) {
                matchedPages.add(pageNum);
                const localStart = Math.max(0, matchStart - start);
                const localEnd = Math.min(end - start, matchEnd - start);
                const ranges = pageHighlightRanges.get(pageNum) ?? [];
                ranges.push([localStart, localEnd]);
                pageHighlightRanges.set(pageNum, ranges);
            }
        }
    }
    return { pagesWithMatches: [...matchedPages].sort((a, b) => a - b), pageHighlightRanges };
}

function computeHighlightMap(pageHighlightRanges: Map<number, [number, number][]>, pageItems: Map<number, string[]>): HighlightMap {
    const map: HighlightMap = new Map();

    for (const [pageNum, ranges] of pageHighlightRanges) {
        const items = pageItems.get(pageNum);
        if (!items) continue;

        const itemHighlights = new Map<number, [number, number][]>();
        const itemOffsets: number[] = [];
        let offset = 0;
        for (let i = 0; i < items.length; i++) {
            itemOffsets.push(offset);
            offset += items[i].length;
        }

        for (const [matchStart, matchEnd] of ranges) {
            for (let i = 0; i < items.length; i++) {
                const itemStart = itemOffsets[i];
                const itemEnd = itemStart + items[i].length;

                if (itemEnd > matchStart && itemStart < matchEnd) {
                    const hlStart = Math.max(0, matchStart - itemStart);
                    const hlEnd = Math.min(items[i].length, matchEnd - itemStart);
                    const existing = itemHighlights.get(i) ?? [];
                    existing.push([hlStart, hlEnd]);
                    itemHighlights.set(i, existing);
                }
            }
        }

        if (itemHighlights.size > 0) {
            map.set(pageNum, itemHighlights);
        }
    }

    return map;
}

const CenterDiv = styled(Box)({
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1,
});

export interface IPdfViewerProps {
    blobUrl?: string;
    onDownload: () => void;
    isDownloading: boolean;
    zoomLevel: number;
    zoomInputValue: string;
    onZoomBy?: (factor: number) => void;
    onZoomIn: () => void;
    onZoomOut: () => void;
    onZoomInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onZoomInputKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
    onZoomInputBlur: () => void;
    isOpen: boolean;
    isLoading?: boolean;
    documentNavigation?: IDocumentNavigation;
    toolbarAction?: IDocumentPreviewToolbarAction;
    initialPage?: number;
    initialSearchText?: string;
    citationChunkContent?: string;
    onCitationResolved?: (found: boolean) => void;
    /** Whether the PDF carries a text layer at all. False for a scan, which has nothing to select. */
    onTextLayerDetected?: (hasText: boolean) => void;
    textSelection?: ITextSelectionConfig;
    /** Resolves the section a selection sits in. The text layer has no heading structure to read it from. */
    resolveSection?: SectionResolver;
}

export default function PdfViewer(props: IPdfViewerProps) {
    const styles = useDocumentPreviewStyles();
    const [numPages, setNumPages] = useState<number>(0);
    const [isPdfDocumentLoading, setIsPdfDocumentLoading] = useState<boolean>(false);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [pageInputValue, setPageInputValue] = useState<string>("1");
    const [containerHeight, setContainerHeight] = useState<number>(0);
    const [containerWidth, setContainerWidth] = useState<number>(0);
    const [pageAspectRatio, setPageAspectRatio] = useState<number>();
    const [searchText, setSearchText] = useState<string>(props.initialSearchText ?? "");
    const [debouncedSearchText, setDebouncedSearchText] = useState<string>(props.initialSearchText ?? "");
    const [pagesWithMatches, setPagesWithMatches] = useState<number[]>([]);
    const [pageHighlightRanges, setPageHighlightRanges] = useState<Map<number, [number, number][]>>(new Map());
    const [pageCitations, setPageCitations] = useState<Map<number, Array<PageCitation>>>(new Map());
    const [currentMatchIndex, setCurrentMatchIndex] = useState<number>(-1);
    const [pageTexts, setPageTexts] = useState<Map<number, string>>(new Map());
    const [pageItems, setPageItems] = useState<Map<number, string[]>>(new Map());
    const containerRef = useRef<HTMLDivElement | null>(null);
    useCtrlWheelZoom(containerRef, props.onZoomBy);
    const listRef = useRef<ListImperativeAPI | null>(null);
    const isManualNavigationRef = useRef<boolean>(false);
    const navigationTimeoutRef = useRef<number | null>(null);
    const currentPageRef = useRef<number>(1);
    const lastScrolledCitationRef = useRef<string | undefined>(undefined);
    const lastResolvedCitationRef = useRef<string | undefined>(undefined);
    // Held in a ref rather than listed as a dependency: the callback is rebuilt on every parent render,
    // and the citation effect writes fresh Maps into state, so depending on it would loop.
    const onCitationResolvedRef = useRef(props.onCitationResolved);
    onCitationResolvedRef.current = props.onCitationResolved;

    // Lets the text-layer probe tell "this document is still the one on screen" from "the user has moved
    // on", which its closure over props alone cannot answer once it has been running for a while.
    const blobUrlRef = useRef(props.blobUrl);
    blobUrlRef.current = props.blobUrl;

    // Stable reference for react-pdf — module-level constants get recreated on Vite HMR,
    // which triggers react-pdf's "options changed but is deep-equal" warning. useMemo with
    // empty deps survives HMR via fast-refresh and keeps the reference stable.
    // The Content Security Policy has no 'unsafe-eval', so pdf.js must not reach for eval.
    // This only affects PDF Type 4 (PostScript calculator) functions: instead of compiling
    // them to JS, pdf.js interprets them, which renders identically and is already the path
    // taken whenever its compiler cannot handle a function.
    const pdfOptions = useMemo(() => ({ wasmUrl: PDF_WASM_URL, isEvalSupported: false }), []);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const resizeObserver = new ResizeObserver((entries) => {
            const entry = entries[0];
            if (entry) {
                const newHeight = entry.contentRect.height;
                const newWidth = entry.contentRect.width;
                setContainerHeight((prev) => (prev !== newHeight ? newHeight : prev));
                setContainerWidth((prev) => (prev !== newWidth ? newWidth : prev));
            }
        });

        resizeObserver.observe(container);
        return () => resizeObserver.disconnect();
    }, []);

    useEffect(() => {
        if (!props.isOpen) {
            setNumPages(0);
            setIsPdfDocumentLoading(false);
            setCurrentPage(1);
            setPageInputValue("1");
            isManualNavigationRef.current = false;
            currentPageRef.current = 1;
            if (navigationTimeoutRef.current) {
                window.clearTimeout(navigationTimeoutRef.current);
                navigationTimeoutRef.current = null;
            }
        }
    }, [props.isOpen]);

    useEffect(() => {
        return () => {
            if (navigationTimeoutRef.current) {
                window.clearTimeout(navigationTimeoutRef.current);
            }
        };
    }, []);

    useDocumentKeyboardNavigation(props.documentNavigation);

    const goToPage = useCallback(
        (pageNumber: number) => {
            const validPage = Math.max(1, Math.min(pageNumber, numPages));
            setCurrentPage(validPage);
            setPageInputValue(String(validPage));
            currentPageRef.current = validPage;

            isManualNavigationRef.current = true;
            if (navigationTimeoutRef.current) {
                window.clearTimeout(navigationTimeoutRef.current);
            }
            navigationTimeoutRef.current = window.setTimeout(() => {
                isManualNavigationRef.current = false;
            }, NAVIGATION_DEBOUNCE_MS);

            listRef.current?.scrollToRow({ index: validPage - 1, align: "start" });
        },
        [numPages]
    );

    useEffect(() => {
        if (numPages <= 1) return;

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "ArrowDown" && currentPageRef.current < numPages) {
                event.preventDefault();
                goToPage(currentPageRef.current + 1);
            } else if (event.key === "ArrowUp" && currentPageRef.current > 1) {
                event.preventDefault();
                goToPage(currentPageRef.current - 1);
            }
        };

        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [numPages, goToPage]);

    const onDocumentLoadSuccess = async (pdfDocument: pdfjs.PDFDocumentProxy) => {
        setNumPages(pdfDocument.numPages);
        setIsPdfDocumentLoading(false);

        // Page one's shape is what the row height and the fitted page size are derived from. Sizing a page by
        // the viewer's height alone makes it wider than a narrow panel, which is what cropped it.
        const firstPage = await pdfDocument.getPage(1);
        const firstPageViewport = firstPage.getViewport({ scale: 1 });
        setPageAspectRatio(firstPageViewport.width / firstPageViewport.height);

        try {
            const texts = new Map<number, string>();
            const items = new Map<number, string[]>();
            let hasTextLayer = false;
            for (let i = 1; i <= pdfDocument.numPages; i++) {
                const page = await pdfDocument.getPage(i);
                const textContent = await page.getTextContent();
                const pageItemStrs = textContent.items.map((item: any) => {
                    if (item.hasEOL && item.str.length > 0 && !item.str.endsWith(" ")) {
                        return item.str + " ";
                    }
                    return item.str;
                });
                const pageText = pageItemStrs.join("");
                items.set(i, pageItemStrs);
                texts.set(i, pageText);

                // A scan has an empty text layer throughout, so the opening pages settle the question.
                // A cover page can legitimately be image-only, hence looking at more than the first.
                if (pageText.trim().length > 0) {
                    hasTextLayer = true;
                }

                // Nothing consumes these maps once the caller switches to the text view, and walking the
                // remaining pages of a long scan only delays that switch.
                if (!hasTextLayer && i >= TEXT_LAYER_PROBE_PAGES) {
                    props.onTextLayerDetected?.(false);
                    return;
                }
            }
            setPageTexts(texts);
            setPageItems(items);
            props.onTextLayerDetected?.(hasTextLayer);
        } catch {
            // A document destroyed by switching documents quickly is expected, and the stale probe has
            // nothing left to say about it. A failure on the document still on screen is different:
            // leaving the question unanswered keeps the pdf viewer mounted over a text layer that
            // selection cannot read, silently, so call it "no text layer" and let the extracted-text
            // view take over.
            if (blobUrlRef.current === props.blobUrl) {
                props.onTextLayerDetected?.(false);
            }
        }
    };

    // react-pdf parses + renders the blob after React Query's fetch completes. Without tracking
    // this phase explicitly the toolbar/viewer area shows blank space between `isLoading` going
    // false and the first page painting — the existing `loading=""` on <Document> intentionally
    // suppresses react-pdf's default spinner because we own the spinner here.
    useEffect(() => {
        if (props.blobUrl) setIsPdfDocumentLoading(true);
    }, [props.blobUrl]);

    useEffect(() => {
        if (numPages > 0 && props.initialPage && props.initialPage <= numPages) {
            goToPage(props.initialPage);
        }
    }, [numPages, props.initialPage, goToPage]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchText(searchText);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchText]);

    useEffect(() => {
        if (pageTexts.size === 0) {
            setPagesWithMatches([]);
            setPageHighlightRanges(new Map());
            setPageCitations(new Map());
            setCurrentMatchIndex(-1);
            return;
        }

        // Searchbar mode: <mark> highlights via customTextRenderer + the existing pageHighlightRanges
        // pipeline. We jump to the first matched page because that's the user-initiated search intent.
        if (debouncedSearchText.length >= MIN_SEARCH_LENGTH) {
            const result = searchDocument(debouncedSearchText, pageTexts);
            setPageCitations(new Map());
            if (result.pagesWithMatches.length === 0) {
                setPagesWithMatches([]);
                setPageHighlightRanges(new Map());
                setCurrentMatchIndex(-1);
                return;
            }
            setPageHighlightRanges(result.pageHighlightRanges);
            setPagesWithMatches(result.pagesWithMatches);

            let initialMatchIndex = 0;
            if (props.initialPage) {
                const pageMatchIndex = result.pagesWithMatches.indexOf(props.initialPage);
                if (pageMatchIndex !== -1) initialMatchIndex = pageMatchIndex;
            }
            setCurrentMatchIndex(initialMatchIndex);
            goToPage(result.pagesWithMatches[initialMatchIndex]);
            return;
        }

        // Citation mode: each region of the citation gives every page it touches a role ('single' /
        // 'start' / 'middle' / 'end') determined by which page contains its start anchor and which
        // contains its end anchor. Per-page PageRow consumes those and walks its own rendered text
        // layer to find the anchors (or, for 'middle' pages, simply highlights everything). No
        // item-index assumption.
        const anchorSets = deriveCitationAnchorSets(props.citationChunkContent);
        const citations = new Map<number, Array<PageCitation>>();
        for (const anchors of anchorSets) {
            for (const [pageNumber, role] of computeCitationPageRoles(anchors, pageTexts)) {
                citations.set(pageNumber, [...(citations.get(pageNumber) ?? []), { anchors, role }]);
            }
        }
        setPageHighlightRanges(new Map());
        setPageCitations(citations);
        const matchedPages = [...citations.keys()].sort((a, b) => a - b);
        setPagesWithMatches(matchedPages);

        // Reported once per citation, and only now that `pageTexts` are loaded: an empty map before
        // then means "not looked yet", not "not in this document".
        if (props.citationChunkContent && props.citationChunkContent !== lastResolvedCitationRef.current) {
            onCitationResolvedRef.current?.(matchedPages.length > 0);
            lastResolvedCitationRef.current = props.citationChunkContent;
        }

        if (matchedPages.length === 0) {
            setCurrentMatchIndex(-1);
            return;
        }
        let initialMatchIndex = 0;
        if (props.initialPage) {
            const pageMatchIndex = matchedPages.indexOf(props.initialPage);
            if (pageMatchIndex !== -1) initialMatchIndex = pageMatchIndex;
        }
        setCurrentMatchIndex(initialMatchIndex);

        // Auto-scroll fires once per unique citation (guarded by lastScrolledCitationRef) so
        // re-runs of this effect don't snap the view back when the user has scrolled away.
        if (props.citationChunkContent && props.citationChunkContent !== lastScrolledCitationRef.current) {
            goToPage(matchedPages[initialMatchIndex]);
            lastScrolledCitationRef.current = props.citationChunkContent;
        }
    }, [debouncedSearchText, props.citationChunkContent, pageTexts, props.initialPage, goToPage]);

    const highlightMap = useMemo(() => computeHighlightMap(pageHighlightRanges, pageItems), [pageHighlightRanges, pageItems]);

    // 100% means the whole page is visible, so the height the viewer offers is capped by what its width can
    // actually show. Without the cap a portrait page in a narrow panel is sized to the height and spills off
    // both sides. Zooming past 100% then scrolls, as before.
    const fittedPageHeight = pageAspectRatio && containerWidth > 0 ? Math.min(containerHeight, (containerWidth - PAGE_GAP) / pageAspectRatio) : containerHeight;

    const getRowHeight = useCallback(() => {
        return Math.round(fittedPageHeight * props.zoomLevel) + PAGE_GAP;
    }, [fittedPageHeight, props.zoomLevel]);

    const handlePageInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setPageInputValue(event.target.value);
    };

    const handlePageInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") {
            const pageNumber = parseInt(pageInputValue, 10);
            if (!isNaN(pageNumber)) {
                goToPage(pageNumber);
            }
        }
    };

    const handlePageInputBlur = () => {
        const pageNumber = parseInt(pageInputValue, 10);
        if (!isNaN(pageNumber)) {
            goToPage(pageNumber);
        } else {
            setPageInputValue(String(currentPage));
        }
    };

    const goToPreviousPage = () => {
        if (currentPage > 1) {
            goToPage(currentPage - 1);
        }
    };

    const goToNextPage = () => {
        if (currentPage < numPages) {
            goToPage(currentPage + 1);
        }
    };

    const goToPreviousMatch = () => {
        if (pagesWithMatches.length === 0) return;
        const newIndex = currentMatchIndex > 0 ? currentMatchIndex - 1 : pagesWithMatches.length - 1;
        setCurrentMatchIndex(newIndex);
        goToPage(pagesWithMatches[newIndex]);
    };

    const goToNextMatch = () => {
        if (pagesWithMatches.length === 0) return;
        const newIndex = currentMatchIndex < pagesWithMatches.length - 1 ? currentMatchIndex + 1 : 0;
        setCurrentMatchIndex(newIndex);
        goToPage(pagesWithMatches[newIndex]);
    };

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchText(event.target.value);
    };

    const handleRowsRendered = useCallback((visibleRows: { startIndex: number; stopIndex: number }) => {
        if (isManualNavigationRef.current) {
            return;
        }
        const newPage = visibleRows.startIndex + 1;
        if (newPage !== currentPageRef.current) {
            setCurrentPage(newPage);
            setPageInputValue(String(newPage));
            currentPageRef.current = newPage;
        }
    }, []);

    const hasSearch = pageHighlightRanges.size > 0;

    const rowProps = useMemo(
        () => ({
            pageHeight: fittedPageHeight,
            zoomLevel: props.zoomLevel,
            pageWrapperStyle: styles.pageWrapper,
            highlightMap,
            hasSearch,
            pageCitations,
        }),
        [fittedPageHeight, props.zoomLevel, styles.pageWrapper, highlightMap, hasSearch, pageCitations]
    );

    const renderDocumentContent = () => {
        if (containerHeight > 0 && containerWidth > 0) {
            return (
                <List<PageRowProps>
                    listRef={listRef}
                    rowComponent={PageRow}
                    rowCount={numPages}
                    rowHeight={getRowHeight}
                    rowProps={rowProps}
                    onRowsRendered={handleRowsRendered}
                    overscanCount={1}
                    style={{ height: containerHeight, width: containerWidth }}
                />
            );
        }

        return null;
    };

    return (
        <Box sx={styles.pdfContainer}>
            <DocumentPreviewToolbar
                zoomLevel={props.zoomLevel}
                zoomInputValue={props.zoomInputValue}
                onZoomIn={props.onZoomIn}
                onZoomOut={props.onZoomOut}
                onZoomInputChange={props.onZoomInputChange}
                onZoomInputKeyDown={props.onZoomInputKeyDown}
                onZoomInputBlur={props.onZoomInputBlur}
                onDownload={props.onDownload}
                isDownloading={props.isDownloading}
                pageSelector={{
                    currentPage,
                    numPages,
                    pageInputValue,
                    onPageInputChange: handlePageInputChange,
                    onPageInputKeyDown: handlePageInputKeyDown,
                    onPageInputBlur: handlePageInputBlur,
                    onPreviousPage: goToPreviousPage,
                    onNextPage: goToNextPage,
                }}
                documentNavigation={props.documentNavigation}
                toolbarAction={props.toolbarAction}
                searchProps={{
                    searchText,
                    onSearchChange: handleSearchChange,
                    matchCount: pagesWithMatches.length,
                    currentMatchIndex,
                    onPreviousMatch: goToPreviousMatch,
                    onNextMatch: goToNextMatch,
                }}
            />
            <Box sx={styles.pdfViewerContainer} ref={containerRef}>
                {(props.isLoading || isPdfDocumentLoading) && (
                    <CenterDiv>
                        <DotLegalFullScreenSpinner />
                    </CenterDiv>
                )}
                {props.blobUrl && (
                    <Document
                        file={props.blobUrl}
                        onLoadSuccess={onDocumentLoadSuccess}
                        onLoadError={() => setIsPdfDocumentLoading(false)}
                        loading=""
                        options={pdfOptions}
                        error={
                            <CenterDiv>
                                <DocumentPreviewFailed />
                            </CenterDiv>
                        }
                    >
                        {renderDocumentContent()}
                    </Document>
                )}
                {/* Rendered here rather than inside a page: selection state must not reach the virtualised
                    list's row props, or react-pdf rebuilds the text layer and drops the selection. */}
                {props.textSelection && (
                    <TextSelectionLayer
                        config={props.textSelection}
                        rootRef={containerRef}
                        domConfig={PDF_TEXT_SELECTION_DOM_CONFIG}
                        resolveSection={props.resolveSection}
                        resetKey={`${props.blobUrl}-${props.zoomLevel}`}
                    />
                )}
            </Box>
        </Box>
    );
}
